import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowCounterClockwise,
  CaretDown,
  CaretRight,
  ChatCenteredDots,
} from 'phosphor-react';
import { toast } from 'sonner';
import { IKanbanCardArquivado, useKanban } from '../../../../hooks/useKanban';
import { ILegalizacao, TipoLegalizacao } from '../../../../hooks/useLegalizacao';
import { formatarCnpj } from '../legalizacaoPlaybook';
import { EtapaReadOnly } from './EtapaReadOnly';

interface LegalizacaoArquivadosProps {
  projetoId?: number;
  /** Tipo do quadro atual — o `EtapaReadOnly` precisa pra resolver o playbook. */
  tipo: TipoLegalizacao;
  /**
   * Mapa cardId → processo, vindo da tela. Arquivar não toca na legalização,
   * então ela continua na lista e dá pra mostrar os dados do processo aqui —
   * sem isso o operador só veria título e data, sem saber o que é o processo.
   */
  legByCardId?: Map<number, ILegalizacao>;
}

/** Campos do processo que valem no resumo do arquivado (e do finalizado). */
export const infoDoProcesso = (leg?: ILegalizacao): { rotulo: string; valor: string }[] => {
  if (!leg) return [];
  const itens: { rotulo: string; valor: string }[] = [];
  if (leg.cnpj) itens.push({ rotulo: 'CNPJ', valor: formatarCnpj(leg.cnpj) ?? leg.cnpj });
  if (leg.nire) itens.push({ rotulo: 'NIRE', valor: leg.nire });
  if (leg.junta_sigla || leg.junta_uf) {
    itens.push({ rotulo: 'Junta', valor: leg.junta_sigla || (leg.junta_uf as string) });
  }
  if (leg.municipio) itens.push({ rotulo: 'Município', valor: leg.municipio });
  if (leg.rota) itens.push({ rotulo: 'Rota', valor: leg.rota });
  if (leg.gop) itens.push({ rotulo: 'GOP', valor: leg.gop });
  if (leg.gon) itens.push({ rotulo: 'GON', valor: leg.gon });
  if (leg.protocolo_numero) itens.push({ rotulo: 'Protocolo', valor: leg.protocolo_numero });
  if (leg.responsavel_nome) itens.push({ rotulo: 'Responsável', valor: leg.responsavel_nome });
  return itens;
};

export const formatarData = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

/**
 * Chip "Arquivados (N)" para a barra de ferramentas + o modal com a lista.
 *
 * Fica junto da busca/filtro de propósito: antes isso era um bloco recolhido
 * ABAIXO do board, onde ninguém achava e o total não aparecia em lugar nenhum.
 *
 * Arquivar tira o CARD do quadro (`arquivado_em`); a legalização continua
 * intacta no banco — por isso é reversível e distinto de excluir.
 */
export const LegalizacaoArquivados: React.FC<LegalizacaoArquivadosProps> = ({
  projetoId,
  tipo,
  legByCardId,
}) => {
  const queryClient = useQueryClient();
  const { getCardsArquivados, desarquivarCard } = useKanban();
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Qual item está expandido para leitura (só um por vez).
  const [abertoId, setAbertoId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['legalizacao-arquivados', projetoId],
    queryFn: () => getCardsArquivados(projetoId as number),
    enabled: !!projetoId,
  });

  // O "Finalizar" também arquiva o card; esses moram em "Finalizados", não aqui.
  const cards: IKanbanCardArquivado[] = (data?.data ?? []).filter(
    c => !legByCardId?.get(c.id)?.finalizado_em,
  );

  const desarquivarMutation = useMutation({
    mutationFn: (cardId: number) => desarquivarCard(cardId),
    onSuccess: () => {
      toast.success('Processo restaurado para o quadro');
      queryClient.invalidateQueries({ queryKey: ['legalizacao-arquivados'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      queryClient.invalidateQueries({ queryKey: ['legalizacoes'] });
    },
    onError: () => toast.error('Não foi possível restaurar o processo.'),
  });

  const total = cards.length;

  return (
    <>
      <Tooltip
        label={
          total === 0
            ? 'Nenhum processo arquivado neste quadro'
            : 'Ver e restaurar processos arquivados'
        }
        openDelay={400}
      >
        {/* DISCRETO de propósito (2026-08-13). Antes era pílula roxa sempre que houvesse
            ao menos 1 arquivado — ou seja, quase sempre — e virava o elemento mais forte
            da barra. O resultado era a hierarquia invertida: os três filtros, que recortam
            o quadro que está na tela, liam como texto solto, e o que gritava era um atalho
            para OUTRA tela, consultado de vez em nunca. Cinza, à direita e fora do grupo
            de filtros: continua achável, sem competir. A contagem segue visível, que é o
            motivo de ele morar aqui e não num menu. */}
        <Button
          size="sm"
          fontSize="13px"
          variant="ghost"
          onClick={onOpen}
          isDisabled={total === 0}
          leftIcon={<Archive size={14} weight="duotone" />}
          borderRadius="full"
          color="gray.500"
          _hover={{ bg: 'blackAlpha.50', color: 'gray.700' }}
          fontWeight="500"
        >
          Arquivados ({isLoading ? '…' : total})
        </Button>
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader display="flex" alignItems="center" gap={2} fontSize="18px" fontWeight="700">
            <Archive size={18} weight="duotone" />
            Processos arquivados
            <Tag size="sm" fontSize="10px" colorScheme="purple" borderRadius="full" ml={1}>
              {total}
            </Tag>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody pb={5}>
            <Text fontSize="11px" color="gray.500" mb={4}>
              Saíram do quadro mas continuam preservados. Restaurar devolve o processo à mesma etapa
              em que estava.
            </Text>

            {isLoading && (
              <Flex justify="center" py={10}>
                <Spinner size="lg" color="purple.500" thickness="3px" />
              </Flex>
            )}

            <Flex direction="column" gap={2}>
              {cards.map(card => {
                const leg = legByCardId?.get(card.id);
                const infos = infoDoProcesso(leg);
                return (
                  <Box
                    key={card.id}
                    bg="white"
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="lg"
                    px={4}
                    py={3}
                  >
                    <Flex align="flex-start" justify="space-between" gap={3}>
                      <Box minW={0} flex={1}>
                        <Text fontSize="13px" fontWeight="700" color="gray.800" noOfLines={1}>
                          {card.titulo}
                        </Text>

                        <Flex align="center" gap={2} mt={1} flexWrap="wrap">
                          <Tooltip label="Etapa em que estava ao ser arquivado" openDelay={400}>
                            <Tag size="sm" colorScheme="purple" borderRadius="full" fontSize="10px">
                              {card.coluna_titulo}
                            </Tag>
                          </Tooltip>
                          <Text fontSize="11px" color="gray.400">
                            {formatarData(card.arquivado_em)}
                            {card.arquivado_por ? ` · por ${card.arquivado_por}` : ''}
                          </Text>
                        </Flex>

                        {infos.length > 0 && (
                          <Flex mt={2} flexWrap="wrap" columnGap={4} rowGap={1}>
                            {infos.map(i => (
                              <Text key={i.rotulo} fontSize="11px" color="gray.500">
                                <Text as="span" color="gray.400">
                                  {i.rotulo}:{' '}
                                </Text>
                                <Text as="span" fontWeight="500" color="gray.700">
                                  {i.valor}
                                </Text>
                              </Text>
                            ))}
                          </Flex>
                        )}

                        {card.motivo && (
                          <Flex align="flex-start" gap={1.5} mt={2}>
                            <Box color="gray.400" mt="2px" flexShrink={0}>
                              <ChatCenteredDots size={13} weight="duotone" />
                            </Box>
                            <Text fontSize="11px" color="gray.600" fontStyle="italic">
                              {card.motivo}
                            </Text>
                          </Flex>
                        )}
                      </Box>

                      <Flex direction="column" gap={1.5} flexShrink={0} align="stretch">
                        <Button
                          size="sm"
                          fontSize="13px"
                          variant="outline"
                          colorScheme="purple"
                          leftIcon={<ArrowCounterClockwise size={14} weight="bold" />}
                          onClick={() => desarquivarMutation.mutate(card.id)}
                          isLoading={
                            desarquivarMutation.isPending &&
                            desarquivarMutation.variables === card.id
                          }
                        >
                          Restaurar
                        </Button>
                        {leg && (
                          <Button
                            size="xs"
                            fontSize="13px"
                            variant="ghost"
                            color="gray.500"
                            leftIcon={
                              abertoId === card.id ? (
                                <CaretDown size={12} weight="bold" />
                              ) : (
                                <CaretRight size={12} weight="bold" />
                              )
                            }
                            onClick={() => setAbertoId(abertoId === card.id ? null : card.id)}
                          >
                            {abertoId === card.id ? 'Ocultar' : 'Detalhes'}
                          </Button>
                        )}
                      </Flex>
                    </Flex>

                    {leg && (
                      <Collapse in={abertoId === card.id} animateOpacity>
                        <Divider my={3} />
                        {/* Etapa em que o processo estava ao ser arquivado, só leitura. */}
                        <EtapaReadOnly legalizacao={leg} tipo={tipo} coluna={card.coluna_titulo} />
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
